import type { ApiClient } from '@carbonwebmail/api-client'
import { useAuth } from '@clerk/react'
import { useState } from 'react'
import { Alert } from '../../ui/Alert.js'
import { AuthLayout } from '../../ui/AuthLayout.js'
import { Field } from '../../ui/Field.js'
import { PillButton } from '../../ui/PillButton.js'
import { useCreateOrganisation } from './useCreateOrganisation.js'

/**
 * The one screen between an identity and a product.
 *
 * Somebody reaches it signed in with the identity provider and belonging to
 * nothing here. That is an ordinary state rather than an error — creating an
 * identity and creating a tenant are two different acts, and only the second
 * is this product's — but it is a dead end until they name an organisation, so
 * this is the only thing on screen.
 *
 * A way out is offered as well. Somebody invited to an existing organisation
 * who signed up instead of following their link needs to leave, not to create
 * a second organisation nobody asked for.
 */
export function OnboardingPage({
  client,
  onCreated,
}: {
  readonly client: ApiClient
  /** Re-reads the session, so the app learns there is an organisation now. */
  readonly onCreated: () => Promise<void>
}) {
  const { signOut } = useAuth()
  const { problem, creating, create } = useCreateOrganisation({ client })
  const [name, setName] = useState('')

  return (
    <AuthLayout>
      <h1 className="mb-1.5 font-bold text-[28px] text-t1 -tracking-[0.01em]">
        Name your organisation
      </h1>
      <p className="mb-7 text-[14px] text-t2">
        Your contacts, campaigns and sending domains all live inside it. You can change the name
        later.
      </p>

      {problem === undefined ? null : <Alert>{problem}</Alert>}

      <form
        onSubmit={(event) => {
          event.preventDefault()
          void create(name).then(async (created) => {
            // Re-read before anything navigates, like signing in: until the
            // app knows there is an organisation, every route sends it back
            // here.
            if (created) await onCreated()
          })
        }}
      >
        <Field
          id="organisation-name"
          label="Organisation name"
          type="text"
          autoComplete="organization"
          value={name}
          disabled={creating}
          onChange={setName}
        />

        <PillButton type="submit" variant="primary" disabled={creating || name.trim() === ''}>
          {creating ? 'Setting up…' : 'Create organisation'}
        </PillButton>
      </form>

      <p className="mt-7 text-[13px] text-t3">
        Joining a team instead? Open the invitation link you were sent.{' '}
        <button
          type="button"
          className="font-semibold text-brand underline"
          onClick={() => void signOut()}
        >
          Sign out
        </button>
      </p>
    </AuthLayout>
  )
}
